import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getSrcSet, getSizes, getResponsiveImage, buildCmsUrl } from "../../utils/imageHelper";



/* Hook para detectar si un elemento está en el viewport */
const useOnScreen = (
  ref: React.RefObject<Element>,
  threshold = 0.1,
  rootMargin = "0px"
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      { threshold, rootMargin }
    );

    observer.observe(ref.current);

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref, threshold, rootMargin]);

  return isIntersecting;
};

interface PortafolioComponentProps {
  portafolios: any;
  inView: boolean;
}

const PortafolioComponent: React.FC<PortafolioComponentProps> = ({
  portafolios,
  inView,
}) => {
  useEffect(() => {
    if (!inView) return;
    /* console.log("PortafolioComponent es visible y useEffect se ejecuta"); */

  }, [inView]);

  // ✅ calculamos índice relativo solo para las tarjetas pequeñas
  let smallIndex = 0;

  return (
    <div className="portafolio-home-component">
      <PortfolioTitle />
      <div className="cards-portafolio-home-component">
        {portafolios.map((item: any, index: number) => {
          if (!item.wide) {
            // asignamos clase según el índice relativo
            const positionClass = smallIndex % 2 === 0 ? "left" : "right";
            smallIndex++;
            return <PortfolioCard key={index} item={item} positionClass={positionClass} />;
          }
          return <PortfolioCard key={index} item={item} />;
        })}
      </div>
    </div>
  );
};

const PortfolioTitle: React.FC = () => {
  const ref = useRef<HTMLHeadingElement>(null); // Cambiado de HTMLDivElement a HTMLHeadingElement
  const isVisible = useOnScreen(ref, 0.1);

  return (
    <h2 // Cambiado de div a h2
      ref={ref}
      className={`title-portafolio-home-component ${isVisible ? "visible" : ""}`}
    >
      Nuestro portafolio
    </h2>
  );
};

interface PortfolioCardProps {
  item: any;
  positionClass?: string; // nueva prop opcional
}

const PortfolioCard: React.FC<PortfolioCardProps> = ({ item, positionClass }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(ref, 0.1, "0px 0px -100px 0px");
  const [timeLeft, setTimeLeft] = useState("00:00:00");

  useEffect(() => {
    if (!item?.vehicle?.price_special_deadline) return;
    const countdownDate = new Date(item.vehicle.price_special_deadline).getTime();

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const distance = countdownDate - now;

      if (distance > 0) {
        const totalHours = Math.floor(distance / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        const formattedTime = `${String(totalHours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
        setTimeLeft(formattedTime);
      } else {
        setTimeLeft("00:00:00");
      }
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [item]);

  const cardClass = item.wide
    ? "large-individual-card-portafolio-home-component"
    : `small-individual-card-portafolio-home-component ${positionClass || ""}`;

  return (
    <div
      ref={ref}
      className={`${cardClass} ${isVisible ? "visible" : ""}`}
      style={{
        backgroundImage: `url(${getResponsiveImage(item?.image?.url || '').desktop})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
      }}

    >
      {isVisible && (
        <>
          <div
            className={
              item.wide
                ? "text-large-individual-card-portafolio-home-component"
                : "text-small-individual-card-portafolio-home-component"
            }
          >
            <h3 className={`vehicle-title ${isVisible ? "visible" : ""}`}>
              {item?.title ? item?.title : item?.vehicle?.name}
            </h3>
            <p className={isVisible ? "visible" : ""}>
              Desde: {item?.vehicle?.price} cop
            </p>
            {item.vehicle.price_special && (
              <p className={isVisible ? "visible" : ""}>
                Oferta especial: {item?.vehicle?.price_special} cop
              </p>
            )}
            {item?.vehicle?.price_special && (
              <p className={isVisible ? "visible contador" : "contador"}>
                {timeLeft}
              </p>
            )}
          </div>

          <div
            className={`${
              item.wide
                ? "accion-large-individual-card-portafolio-home-component"
                : "accion-small-individual-card-portafolio-home-component"
            } ${isVisible ? "visible" : ""}`}
          >
            <img
              loading="lazy"
              src={buildCmsUrl(item?.vehicle?.security_seal?.url)}
              srcSet={getSrcSet(item?.vehicle?.security_seal?.url || '')}
              sizes={getSizes('100px')}
              alt={`Sello de seguridad del ${
                item?.vehicle?.model || "vehículo Hyundai"
              }`}
            />

              <Link
                to={`/vehiculos/${item?.url_live_experience}`}
                className={
                  item.wide
                    ? "btn-vive-la-exp-accion-large-individual-card-portafolio-home-component"
                    : "btn-vive-la-exp-accion-small-individual-card-portafolio-home-component"
                }
              >
                Vive la experiencia
              </Link>

              <div
                className={
                  item.wide
                    ? "btn-opciones-accion-large-individual-card-portafolio-home-component"
                    : "btn-opciones-accion-small-individual-card-portafolio-home-component"
                }
              >
                {item?.url_compare_vehicle && (
                  <Link
                    to={`/vehiculos/${item?.url_compare_vehicle}/comparador/`}
                    className={
                      item.wide
                        ? `btn-compara-accion-large-individual-card-portafolio-home-component ${
                            item?.url_reserve ? "" : "full"
                          }`
                        : `btn-compara-accion-small-individual-card-portafolio-home-component ${
                            item?.url_reserve ? "" : "full"
                          }`
                    }
                  >
                    Compara este vehículo
                  </Link>
                )}
                {item?.url_reserve && (
                  <a
                    href={item?.url_reserve}
                    className={
                      item.wide
                        ? `btn-reserva-accion-large-individual-card-portafolio-home-component ${
                            item?.url_compare_vehicle ? "" : "full"
                          }`
                        : `btn-reserva-accion-small-individual-card-portafolio-home-component ${
                            item?.url_compare_vehicle ? "" : "full"
                          }`
                    }
                  >
                    Reserva en línea
                  </a>
                )}
              </div>
              </div>

          <h5 className={
            item.wide
              ? "condiciones-large-individual-card-portafolio-home-component"
              : "condiciones-small-individual-card-portafolio-home-component"
          }>
            <span>
              *{" "}
              <Link to={"/terminos-condiciones"}>
                Consulta los términos y condiciones aquí.
              </Link>
            </span>
          </h5>
        </>
      )}
    </div>
  );
};

export default PortafolioComponent;
